<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="inner-container">
      <div class="filters">
        <div v-if="activeFillingNo">Filling No: {{ activeFillingNo }}</div>
        <div v-else>
          Date: {{ activeListStartDate.formatString('yyyy-MM-dd') }} to
          {{ activeListEndDate.formatString('yyyy-MM-dd') }}
        </div>
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="220" :isOpen.sync="filterOpen">
          <div class="d-flex flex-column">
            <BoxContainer title="Date" class="d-flex">
              <DateTimePicker placeholder="From" :value.sync="listStartDate" :showTime="false" />
              <DateTimePicker placeholder="To" :value.sync="listEndDate" :showTime="false" />
            </BoxContainer>
            <Button class="ml-auto mt-2" @click="filterRefreshClick">Refresh</Button>
          </div>
        </ButtonFilter>
      </div>
      <TableBox
        :tableData="SSTListTableData"
        :loading="SSTListTableLoading"
        title="SST Return List"
        :selectedRow.sync="SSTListTableSelectedRows"
        class="sst-table"
        :dataType="SSTListTableDataType"
        @rowSelected="sstRowSelected"
        style="min-height: 0;"
      />
      <div class="d-flex align-items-center gap-3">
        <Dropdown
          placeholder="Report Type"
          :items="reportTypes"
          itemKey="label"
          :selectedObject.sync="selectedReportType"
          :errorMessage.sync="selectedReportTypeError"
        />
        <Button
          class="mt-1 ml-auto flex-shrink-0"
          style="width: fit-content"
          :disabled="previewDisable"
          @click="previewClick"
          >Preview SST Return</Button
        >
      </div>
      <BoxContainer
        title="SST Reconciliation "
        v-if="selectedReportType ? selectedReportType.value == 'Reconciliation' : false"
      >
        <Dropdown
          placeholder="Summary/Detail"
          :items="reconTypes"
          itemKey="label"
          :selectedObject.sync="selectedReconType"
          :errorMessage.sync="selectedReconTypeError"
        />
        <CheckBox
          class="ml-2"
          :isChecked.sync="reconIncludeZero"
          :title="'Include documents where unpaid tax amount is zero'"
        />
        <div class="d-flex align-items-center pl-2 mt-1" style="gap: 8px">
          <DateTimePicker
            placeholder="Start Date"
            :showTime="false"
            :value.sync="reconStartDate"
            :errorMessage.sync="reconStartDateError"
            :popupUpwards="true"
          />
          <DateTimePicker placeholder="End Date" :showTime="false" :value.sync="reconEndDate" :popupUpwards="true" />
        </div>
      </BoxContainer>
    </div>
    <div class="inner-container">
      <TableBox v-if="showTable" :tableData="printTableData" :title="printTableTitle" :dataType="printTableDataType" />
      <DesignerV2
        v-else-if="showDesignerV2 && pagerData"
        :viewMode="true"
        :reportData="pagerData"
        reportFolder="ROTForm"
        :reportFile="designerReportFiles"
        :canShare="true"
        :canClose="false"
      />

      <DesignerV3
        v-else-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        ref="designerV3"
      />
      <div v-else class="user-instructions my-auto">
        Select SST Return and click on <strong>Preview SST Return</strong> to get started
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    stateItem() {
      return this.$store.state;
    },
    currencyFormatState() {
      return this.stateItem.currencyFormat;
    },

    previewDisable() {
      const { value: selectedType } = this.selectedReportType || {};
      if (!selectedType) return true;

      if (selectedType == 'Reconciliation') {
        if (!this.selectedReconType || !this.reconStartDate) return true;
      } else if (this.SSTListTableSelectedRows.length == 0) return true;

      return false;
    },
  },
  data: function () {
    return {
      filterOpen: false,

      listStartDate: new Date(new Date().getFullYear(), 0, 1),
      listEndDate: new Date(new Date().getFullYear(), 11, 31),

      activeListStartDate: new Date(new Date().getFullYear(), 0, 1),
      activeListEndDate: new Date(new Date().getFullYear(), 11, 31),

      activeFillingNo: '',

      SSTListTableData: [],
      SSTListTableLoading: false,
      SSTListTableSelectedRows: [],

      SSTListTableDataType: {
        '#': 'number',
        'Filling No': 'string',
        'Filling Date': 'date',
        'Period Start Date': 'date',
        'Period End Date': 'date',
        'Tax Payable': 'currency',
        'Amt Deduct Fr Credit Note': 'currency',
        'Penalty Amt': 'currency',
        'Total Payable Incl Penalty': 'currency',
        Remarks: 'string',
        'User Created': 'string',
        'Date Created': 'date',
        'User Updated': 'string',
        'Date Updated': 'date',
      },

      showDesignerV2: false,
      pagerData: null,

      designerReportFiles: ['SST02.json'],

      reportTypes: [
        { label: 'SST Return', value: 'SST02' },
        { label: 'SST Listing', value: 'SSTListing' },
        { label: 'SST Listing (Tax > 0)', value: 'SSTListingTax' },
        { label: 'Reconciliation Report', value: 'Reconciliation' },
      ],

      selectedReportType: { label: 'SST Return', value: 'SST02' },
      selectedReportTypeError: '',

      reconTypes: [
        {
          label: 'Summary',
          value: 'Summary',
        },
        {
          label: 'Detail',
          value: 'Detail',
        },
      ],

      selectedReconType: {
        label: 'Summary',
        value: 'Summary',
      },
      selectedReconTypeError: '',

      reconIncludeZero: true,
      reconStartDate: null,
      reconEndDate: null,

      reconStartDateError: '',

      showDesignerV3: false,
      designerV3ReportFiles: ['SST02.json'],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      SSTDetails: null,

      showTable: false,

      printTableTitle: '',
      printTableData: [],
      printTableDataType: {},
    };
  },
  props: {},
  methods: {
    async filterRefreshClick() {
      this.activeFillingNo = null;

      if (!this.listStartDate) this.listStartDate = new Date(new Date().getFullYear(), 0, 1);
      if (!this.listEndDate) this.listEndDate = new Date(this.listStartDate.getFullYear(), 11, 31);

      this.activeListStartDate = this.listStartDate;
      this.activeListEndDate = this.listEndDate;

      await this.getSSTReturnList();

      this.filterOpen = false;
    },

    async getSSTReturnList() {
      this.SSTListTableLoading = true;
      const result = await this.$apiCallV2('/SSTReturn/GetSSTReturnList', {
        startdate: this.activeFillingNo ? null : (this.activeListStartDate?.formatString('yyyy-MM-dd') ?? null),
        enddate: this.activeFillingNo ? null : (this.activeListEndDate?.formatString('yyyy-MM-dd') ?? null),
        fillingno: this.activeFillingNo,
      });

      const filterResult = result.map((item, index) => {
        const {
          FillingNo,
          FillingDate,
          FillingPeriodFrom,
          FillingPeriodTo,
          TotalValTaxPayable,
          AmtDeductFrCreditNote,
          PenaltyAmt,
          TotalTaxPayableInclPenalty,
          Remarks1,
          UserCreated,
          DateCreated,
          UserID,
          DateUpdated,
        } = item;

        return {
          '#': index + 1,
          'Filling No': FillingNo,
          'Filling Date': FillingDate ?? null,
          'Period Start Date': FillingPeriodFrom ?? null,
          'Period End Date': FillingPeriodTo ?? null,
          'Tax Payable': TotalValTaxPayable ?? '',
          'Amt Deduct Fr Credit Note': AmtDeductFrCreditNote ?? '',
          'Penalty Amt': PenaltyAmt ?? '',
          'Total Payable Incl Penalty': TotalTaxPayableInclPenalty ?? '',
          Remarks: Remarks1 ?? '',
          'User Created': UserCreated ?? '',
          'Date Created': DateCreated ?? null,
          'User Updated': UserID ?? '',
          'Date Updated': DateUpdated ?? null,
        };
      });

      this.listStartDateActive = this.listStartDate?.formatString('yyyy-MM-dd') ?? null;
      this.listEndDateActive = this.listEndDate?.formatString('yyyy-MM-dd') ?? null;

      this.SSTListTableData = [];
      this.$nextTick(() => {
        this.SSTListTableData = filterResult ?? [];
        this.SSTListTableLoading = false;
      });
    },

    previewClick() {
      this.selectedReportTypeError = '';

      const { value: selectedType, label: selectedTypeLabel } = this.selectedReportType || {};
      if (!selectedType) {
        this.selectedReportTypeError = 'Report Type is required!';
        return;
      }

      this.showTable = false;
      this.showDesignerV2 = false;
      this.showDesignerV3 = false;

      this.printTableDataType = {};

      const {
        'Filling No': fillingno,
        'Period Start Date': startDate,
        'Period End Date': endDate,
      } = this.SSTListTableSelectedRows?.[0] || {};

      if (selectedType == 'Reconciliation') {
        this.reconStartDateError = '';
        if (!this.reconStartDate) {
          this.reconStartDateError = 'Reconciliation Start Date is required!';
          return;
        }
        if (!this.reconEndDate) this.reconEndDate = new Date();

        this.getSSTRecon();

        this.printTableTitle = ` ${selectedTypeLabel} (${new Date(this.reconStartDate).formatString('dd-MM-yyyy')} to ${new Date(this.reconEndDate).formatString('dd-MM-yyyy')})`;

        return;
      }

      if (!fillingno) return;

      this.printTableTitle = ` ${selectedTypeLabel} ${fillingno} (${new Date(startDate).formatString('dd-MM-yyyy')} to ${new Date(endDate).formatString('dd-MM-yyyy')})`;

      if (selectedType == 'SST02') this.getSSTReturnReport(fillingno);
      else if (selectedType == 'SSTListing') this.getSSTListingReport(fillingno, false);
      else if (selectedType == 'SSTListingTax') this.getSSTListingReport(fillingno, true);
    },

    async getSSTReturnReport(fillingno) {
      this.pagerData = null;

      this.$fullscreenLoading('Getting SST Returns');

      const sst = this.parseSSTXML(await this.getSSTReturnsDetail(fillingno));

      const {
        FillingNo,
        CompanyName,
        SalesTaxLicenseNo,
        ServiceTaxLicenseNo,
        ReturnPaymentDueDate,
        TaxPeriodStartDate,
        TaxPeriodEndDate,
        TotalValTaxablePerc5,
        TotalValTaxablePerc10,
        TotalValTaxablePerc6,
        TotalValTaxablePerc8,
        TotalUnitTaxableRM25,
        TotalValTaxPayablePerc5,
        TotalValTaxPayablePerc10,
        TotalValTaxPayablePerc6,
        TotalValTaxPayablePerc8,
        TotalValTaxPayableRM25,
        TotalValTaxPayable,
        AmtDeductFrCreditNote,
        AmtDeductSalesTax,
        AmtDeductServiceTax,
        TotalValBadDebtRelief,
        AmtAdjustmentSalesTax,
        TotalTaxPayableB4Penalty,
        PenaltyAmt,
        TotalTaxPayableInclPenalty,
        LitreRate,
        LitreSoldQty,
        LitreValTaxSales,
        LitreValTaxPayable,
        KGRate,
        KGSoldQty,
        KGValTaxSales,
        KGValTaxPayable,
        AdVolerumRate,
        AdVolValTaxSales,
        AdVolValTaxPayable,
        SalesExemptedExport,
        SalesExemptedSchA,
        SalesExemptedSchB,
        SalesExemptedSchC_Item1and2,
        SalesExemptedSchC_Item3and4,
        SalesExemptedSchC_Item5,
        TotalValExemptedTaxableServices,
        TotalValExemptedGroupRelief,
        TotalValPartialExemptedTaxableServices,
        TotalValNonTaxableServices,
        TotalValExemptedSubsection35,
        PersonExempted_Item1and2,
        PersonExempted_Item3and4,
        PersonExempted_Item5,
        FillingDate,
        DeclarantName,
        DeclarantNRIC,
        DeclarantDesignation,
        DeclarantTelNo,
      } = sst;

      // Helper to split a Date into { d1, d2, m1, m2, y1, y2, y3, y4 }
      const splitDate = (dateVal) => {
        if (!dateVal) return { d1: '', d2: '', m1: '', m2: '', y1: '', y2: '', y3: '', y4: '' };

        const parts = dateVal.split(/[/-]/);
        // if first part is 4 digits, it's yyyy-mm-dd, otherwise dd-mm-yyyy
        const d =
          parts[0].length === 4 ?
            new Date(`${parts[0]}-${parts[1]}-${parts[2]}`)
          : new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);

        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = String(d.getFullYear());
        return {
          d1: dd[0],
          d2: dd[1],
          m1: mm[0],
          m2: mm[1],
          y1: yyyy[0],
          y2: yyyy[1],
          y3: yyyy[2],
          y4: yyyy[3],
        };
      };

      const period = splitDate(TaxPeriodStartDate);
      const periodEnd = splitDate(TaxPeriodEndDate);
      const due = splitDate(ReturnPaymentDueDate);
      const filling = splitDate(FillingDate);

      // Build details rows (up to 8, matching report keys detailsNo1..8)
      const detailsRows = [];
      for (let n = 1; n <= 9; n++) {
        const desc = sst[`TaxItemDesc${n}`];
        if (desc !== null && desc !== undefined) {
          const getVal = (key) => sst[key] ?? 0;
          detailsRows.push({
            no: n,
            desc: (desc ?? '').trim(),
            code: (sst[`TaxItemTariffCode${n}`] ?? '').trim(),
            taxable: getVal(`ValofTaxGoods${n}`),
            used: getVal(`ValofGoodsOwnUse${n}`),
            ser: getVal(`ValTaxServices${n}`),
          });
        }
      }

      const detailsMap = {};
      detailsRows.forEach((row, i) => {
        const n = i + 1;
        detailsMap[`detailsNo${n}`] = row.no;
        detailsMap[`detailsDesc${n}`] = row.desc;
        detailsMap[`detailsCode${n}`] = row.code;

        const active = row.desc || row.code;
        detailsMap[`detailsTaxable${n}`] = active ? row.taxable : '';
        detailsMap[`detailsUsed${n}`] = active ? row.used : '';
        detailsMap[`detailsSer${n}`] = active ? row.ser : '';
      });

      const detailsTaxableTotal = detailsRows.reduce((sum, r) => sum + (parseFloat(r.taxable) || 0), 0);
      const detailsUsedTotal = detailsRows.reduce((sum, r) => sum + (parseFloat(r.used) || 0), 0);
      const detailsSerTotal = detailsRows.reduce((sum, r) => sum + (parseFloat(r.ser) || 0), 0);
      const detailsNetTotal = detailsTaxableTotal + detailsUsedTotal + detailsSerTotal;

      const reports = {
        fillingNo: FillingNo ?? '',

        sstSales: SalesTaxLicenseNo ? 'X' : '',
        sstService:
          SalesTaxLicenseNo ? ''
          : ServiceTaxLicenseNo ? 'X'
          : '',
        sstNo: SalesTaxLicenseNo || ServiceTaxLicenseNo,

        nameOfBusiness: CompanyName ?? '',

        // Tax period start date
        pD1: period.d1,
        pD2: period.d2,
        pM1: period.m1,
        pM2: period.m2,
        pY1: period.y3,
        pY2: period.y4,

        // Return payment due date
        eD1: periodEnd.d1,
        eD2: periodEnd.d2,
        eM1: periodEnd.m1,
        eM2: periodEnd.m2,
        eY1: periodEnd.y3,
        eY2: periodEnd.y4,

        // Filling date (dD = declaration date)
        dD1: due.d1,
        dD2: due.d2,
        dM1: due.m1,
        dM2: due.m2,
        dY1: due.y3,
        dY2: due.y4,

        // Detail rows
        ...detailsMap,

        detailsTaxableTotal: this.$currencyFormat(detailsTaxableTotal),
        detailsUsedTotal: this.$currencyFormat(detailsUsedTotal),
        detailsSerTotal: this.$currencyFormat(detailsSerTotal),
        detailsNetTotal: this.$currencyFormat(detailsNetTotal),

        // Section B2
        b211aVal: TotalValTaxablePerc5,
        b211aTax: TotalValTaxPayablePerc5,
        b211bVal: TotalValTaxablePerc10,
        b211bTax: TotalValTaxPayablePerc10,
        b211cVal: TotalValTaxablePerc6,
        b211cTax: TotalValTaxPayablePerc6,
        b211dVal: TotalValTaxablePerc8,
        b211dTax: TotalValTaxPayablePerc8,
        b211eVal: TotalUnitTaxableRM25,
        b211eTax: TotalValTaxPayableRM25,

        b212Val: TotalValTaxPayable,

        b213aVal: AmtDeductFrCreditNote,
        b213bVal: AmtDeductSalesTax,
        b213cVal: AmtDeductServiceTax,
        b213dVal: TotalValBadDebtRelief,

        b213AVal: AmtAdjustmentSalesTax,
        b214Val: TotalTaxPayableB4Penalty,
        b215Rate:
          Number(TotalTaxPayableB4Penalty) > 0 ?
            parseFloat((Number(PenaltyAmt) / Number(TotalTaxPayableB4Penalty)) * 100).toFixed()
          : 0,
        b215Val: PenaltyAmt,
        b216Val: TotalTaxPayableInclPenalty,

        // Section C17
        c17litre: LitreRate,
        c17litreQty: LitreSoldQty,
        c17litreVal: LitreValTaxSales,
        c17litreTax: LitreValTaxPayable,

        c17kg: KGRate,
        c17kgQty: KGSoldQty,
        c17kgVal: KGValTaxSales,
        c17kgTax: KGValTaxPayable,

        c17vol: AdVolerumRate,
        c17volVal: AdVolValTaxSales,
        c17volTax: AdVolValTaxPayable,

        // Section D18
        d18aVal: SalesExemptedExport,
        d18b1Val: SalesExemptedSchA,
        d18b2Val: SalesExemptedSchB,
        d18b3iVal: SalesExemptedSchC_Item1and2,
        d18b3iiVal: SalesExemptedSchC_Item3and4,
        d18b3iiiVal: SalesExemptedSchC_Item5,

        d18c1Val: TotalValExemptedTaxableServices,
        d18c2Val: TotalValExemptedGroupRelief,
        d18c3Val: TotalValPartialExemptedTaxableServices,

        d18dVal: TotalValNonTaxableServices,
        d18eVal: TotalValExemptedSubsection35,

        // Section E
        e19Val: PersonExempted_Item1and2,
        e20Val: PersonExempted_Item3and4,
        e21Val: PersonExempted_Item5,

        // Signing/declaration date
        sD1: filling.d1,
        sD2: filling.d2,
        sM1: filling.m1,
        sM2: filling.m2,
        sY1: filling.y1,
        sY2: filling.y2,
        sY3: filling.y3,
        sY4: filling.y4,

        declarantName: DeclarantName ?? '',
        declarantIC: DeclarantNRIC ?? '',
        declarantDesig: DeclarantDesignation ?? '',
        declarantPhone: DeclarantTelNo ?? '',
      };

      this.pagerData = reports;

      this.showDesignerV2 = true;

      this.$fullscreenLoading();
    },

    // async getSSTListingReport(fillingno, startDate, endDate, nonZeroTax = false) {
    async getSSTListingReport(fillingno, nonZeroTax = false) {
      this.$fullscreenLoading('Getting SST Listing');

      const result = await this.$apiCallV2('/SSTReturn/PrintSSTListing', {
        fillingno: fillingno,
        nonzerotax: nonZeroTax ? 'Y' : null,
      });

      const recordTypeGroups = [
        { groupName: 'Payment Received for Taxable Services', key: 'R' },
        { groupName: 'Taxable Services for Twelve Months Overdue Invoices / Debit Notes', key: 'I' },
        { groupName: 'Tax Deduction from Credit Note', key: 'C' },
      ];

      this.printTableData = result.map((item, index) => {
        const {
          Recordtype,
          ReceiptDate,
          ReceiptNo,
          InvoiceDate,
          InvoiceNo,
          DebtorName,
          DebtorNo,
          ItemDesc,
          ServiceType,
          ItemCode,
          TaxCode,
          TaxRate,
          Amount,
          TaxAmt,
        } = item;

        const groupName = recordTypeGroups.find((item) => item.key == Recordtype)?.groupName;
        return {
          '#': index + 1,
          'Receipt Date': new Date(ReceiptDate).formatString('yyyy/MM/dd'),
          'Receipt No': ReceiptNo,
          'Invoice Date': new Date(InvoiceDate).formatString('yyyy/MM/dd'),
          'Invoice No': InvoiceNo,
          'Debtor Name': DebtorName,
          'Debtor No': DebtorNo,
          'Item Description': ItemDesc,
          'Service Type': ServiceType,
          'Item Code': ItemCode,
          'Tax Code': TaxCode,
          'Tax Rate': TaxRate,
          Amount: this.$currencyFormat(Amount || 0),
          'Tax Amt': this.$currencyFormat(TaxAmt || 0),
          'Record Type': `${Recordtype} - ${groupName}`,
        };
      });

      const dataType = {
        '#': 'number',
        'Receipt Date': 'date',
        'Receipt No': 'string',
        'Invoice Date': 'date',
        'Invoice No': 'string',
        'Debtor Name': 'string',
        'Debtor No': 'string',
        'Item Description': 'string',
        'Service Type': 'string',
        'Item Code': 'string',
        'Tax Code': 'string',
        'Tax Rate': 'string',
        Amount: 'currency',
        'Tax Amt': 'currency',
        'Record Type': 'string',
      };

      this.printTableDataType = dataType;

      this.showTable = true;

      // const general = {
      //   companyName: this.stateItem.loginDetails.CoName,
      //   printDate: this.reportDate(),
      //   startDate: new Date(startDate).formatString('dd/MM/yyyy'),
      //   endDate: new Date(endDate).formatString('dd/MM/yyyy'),
      //   page: '{{jsreportPageNum}} of {{jsreportTotalPage}}',
      // };

      // const mapItem = (item, index) => {
      //   const {
      //     ReceiptDate,
      //     ReceiptNo,
      //     InvoiceDate,
      //     InvoiceNo,
      //     DebtorName,
      //     DebtorNo,
      //     ItemDesc,
      //     ServiceType,
      //     ItemCode,
      //     TaxCode,
      //     TaxRate,
      //     Amount,
      //     TaxAmt,
      //   } = item;
      //   return {
      //     index: index + 1,
      //     docDate: new Date(ReceiptDate).formatString('dd/MM/yyyy'),
      //     docNo: ReceiptNo,
      //     invDate: new Date(InvoiceDate).formatString('dd/MM/yyyy'),
      //     invNo: InvoiceNo,
      //     debtorName: `${DebtorName}${DebtorNo ? ` (${DebtorNo})` : ''}`,
      //     itemDesc: ItemDesc,
      //     serviceType: ServiceType,
      //     itemCode: ItemCode,
      //     taxCode: TaxCode,
      //     taxRate: TaxRate,
      //     amount: this.$currencyFormat(Amount),
      //     taxAmt: this.$currencyFormat(TaxAmt),
      //     totalAmt: this.$currencyFormat(Number(Amount) + Number(TaxAmt)),
      //   };
      // };

      // const group1 = result.filter((item) => item.Recordtype == 'R').map(mapItem);
      // const group2 = result.filter((item) => item.Recordtype == 'I').map(mapItem);
      // const group3 = result.filter((item) => item.Recordtype == 'C').map(mapItem);

      // const calcRateTotal = (items) => {
      //   const rateMap = {};
      //   items.forEach((item) => {
      //     const rate = item.taxRate;
      //     if (!rateMap[rate]) {
      //       rateMap[rate] = { taxRate: rate, totalAmt: 0, totalTaxAmt: 0, totalTotalAmt: 0 };
      //     }
      //     rateMap[rate].totalAmt += Number(item.amount.replaceAll(/[^0-9.-]/g, ''));
      //     rateMap[rate].totalTaxAmt += Number(item.taxAmt.replaceAll(/[^0-9.-]/g, ''));
      //     rateMap[rate].totalTotalAmt += Number(item.totalAmt.replaceAll(/[^0-9.-]/g, ''));
      //   });
      //   return Object.values(rateMap).map((r) => ({
      //     ...r,
      //     totalAmt: this.$currencyFormat(r.totalAmt),
      //     totalTaxAmt: this.$currencyFormat(r.totalTaxAmt),
      //     totalTotalAmt: this.$currencyFormat(r.totalTotalAmt),
      //   }));
      // };

      // const groups = [
      //   { groupName: '1. Payment Received for Taxable Services', taxItem: group1 },
      //   { groupName: '2. Taxable Services for Twelve Months Overdue Invoices / Debit Notes', taxItem: group2 },
      //   { groupName: '3. Tax Deduction from Credit Note', taxItem: group3 },
      // ]
      //   .filter((g) => g.taxItem.length > 0)
      //   .map((g) => {
      //     const rateTotal = calcRateTotal(g.taxItem);
      //     const totalAmt = g.taxItem.reduce((s, i) => s + Number(i.amount.replaceAll(/[^0-9.-]/g, '')), 0);
      //     const totalTaxAmt = g.taxItem.reduce((s, i) => s + Number(i.taxAmt.replaceAll(/[^0-9.-]/g, '')), 0);
      //     const totalTotalAmt = g.taxItem.reduce((s, i) => s + Number(i.totalAmt.replaceAll(/[^0-9.-]/g, '')), 0);
      //     return {
      //       ...g,
      //       rateTotal,
      //       totalAmt: this.$currencyFormat(totalAmt),
      //       totalTaxAmt: this.$currencyFormat(totalTaxAmt),
      //       totalTotalAmt: this.$currencyFormat(totalTotalAmt),
      //     };
      //   });

      // const sumField = (field) =>
      //   this.$currencyFormat(
      //     groups.reduce(
      //       (sum, g) => sum + g.taxItem.reduce((s, i) => s + Number(i[field].replaceAll(/[^0-9.-]/g, '')), 0),
      //       0,
      //     ),
      //   );

      // const listingItem = {
      //   groupName: groups,
      //   rateTotal: groups.map((g) => ({
      //     groupName: g.groupName,
      //     rateItem: calcRateTotal(g.taxItem),
      //   })),
      //   grandAmt: sumField('amount'),
      //   grandTax: sumField('taxAmt'),
      //   grandTotal: sumField('totalAmt'),
      // };

      // this.designerV3ReportFiles = ['SSTListing.json'];
      // this.designerV3ReportData = [listingItem];
      // this.designerV3SharedData = general;

      // this.showDesignerV3 = true;
      this.$fullscreenLoading();
    },

    async getSSTReturnsDetail(fillingno) {
      const result = await this.$apiCallV2('/SSTReturn/GetSSTReturnsDetail', { fillingno: fillingno });
      return result[0].SST02Details;
    },

    parseSSTXML(xmlString) {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      const currSeperator = this.currencyFormatState.charAt(0);

      const getText = (tag) => xmlDoc.querySelector(tag)?.textContent ?? '';
      const getMoney = (tag) => {
        const get = xmlDoc.querySelector(tag)?.textContent || '0';
        const replace = get.replaceAll(currSeperator, '');

        return this.$currencyFormat(replace);
      };

      const returnObj = {
        // Header
        FillingNo: getText('txtFillingNo'),
        SalesTaxLicenseNo: getText('txtSalesTaxNo'),
        ServiceTaxLicenseNo: getText('txtServiceTaxNo'),
        CompanyName: getText('txtCompany'),
        TaxPeriodStartDate: getText('txtTaxablePeriodStart'),
        TaxPeriodEndDate: getText('txtTaxablePeriodEnd'),
        ReturnPaymentDueDate: getText('txtRetPaymentDueDate'),

        // Part B1
        TaxItemDesc1: getText('TaxItemDesc1'),
        TaxItemTariffCode1: getText('TaxItemTariffCode1'),
        ValofTaxGoods1: getMoney('ValofTaxGoods1'),
        ValofGoodsOwnUse1: getMoney('ValofGoodsOwnUse1'),
        ValTaxServices1: getMoney('ValTaxServices1'),
        TaxPayable1: getMoney('TaxPayable1'),
        AmtDeductCN1: getMoney('AmtDeductCN1'),
        TotalValExempted1: getMoney('TotalValExempted1'),

        TaxItemDesc2: getText('TaxItemDesc2'),
        TaxItemTariffCode2: getText('TaxItemTariffCode2'),
        ValofTaxGoods2: getMoney('ValofTaxGoods2'),
        ValofGoodsOwnUse2: getMoney('ValofGoodsOwnUse2'),
        ValTaxServices2: getMoney('ValTaxServices2'),
        TaxPayable2: getMoney('TaxPayable2'),
        AmtDeductCN2: getMoney('AmtDeductCN2'),
        TotalValExempted2: getMoney('TotalValExempted2'),

        TaxItemDesc3: getText('TaxItemDesc3'),
        TaxItemTariffCode3: getText('TaxItemTariffCode3'),
        ValofTaxGoods3: getMoney('ValofTaxGoods3'),
        ValofGoodsOwnUse3: getMoney('ValofGoodsOwnUse3'),
        ValTaxServices3: getMoney('ValTaxServices3'),
        TaxPayable3: getMoney('TaxPayable3'),
        AmtDeductCN3: getMoney('AmtDeductCN3'),
        TotalValExempted3: getMoney('TotalValExempted3'),

        TaxItemDesc4: getText('TaxItemDesc4'),
        TaxItemTariffCode4: getText('TaxItemTariffCode4'),
        ValofTaxGoods4: getMoney('ValofTaxGoods4'),
        ValofGoodsOwnUse4: getMoney('ValofGoodsOwnUse4'),
        ValTaxServices4: getMoney('ValTaxServices4'),
        TaxPayable4: getMoney('TaxPayable4'),
        AmtDeductCN4: getMoney('AmtDeductCN4'),
        TotalValExempted4: getMoney('TotalValExempted4'),

        TaxItemDesc5: getText('TaxItemDesc5'),
        TaxItemTariffCode5: getText('TaxItemTariffCode5'),
        ValofTaxGoods5: getMoney('ValofTaxGoods5'),
        ValofGoodsOwnUse5: getMoney('ValofGoodsOwnUse5'),
        ValTaxServices5: getMoney('ValTaxServices5'),
        TaxPayable5: getMoney('TaxPayable5'),
        AmtDeductCN5: getMoney('AmtDeductCN5'),
        TotalValExempted5: getMoney('TotalValExempted5'),

        TaxItemDesc6: getText('TaxItemDesc6'),
        TaxItemTariffCode6: getText('TaxItemTariffCode6'),
        ValofTaxGoods6: getMoney('ValofTaxGoods6'),
        ValofGoodsOwnUse6: getMoney('ValofGoodsOwnUse6'),
        ValTaxServices6: getMoney('ValTaxServices6'),
        TaxPayable6: getMoney('TaxPayable6'),
        AmtDeductCN6: getMoney('AmtDeductCN6'),
        TotalValExempted6: getMoney('TotalValExempted6'),

        TaxItemDesc7: getText('TaxItemDesc7'),
        TaxItemTariffCode7: getText('TaxItemTariffCode7'),
        ValofTaxGoods7: getMoney('ValofTaxGoods7'),
        ValofGoodsOwnUse7: getMoney('ValofGoodsOwnUse7'),
        ValTaxServices7: getMoney('ValTaxServices7'),
        TaxPayable7: getMoney('TaxPayable7'),
        AmtDeductCN7: getMoney('AmtDeductCN7'),
        TotalValExempted7: getMoney('TotalValExempted7'),

        TaxItemDesc8: getText('TaxItemDesc8'),
        TaxItemTariffCode8: getText('TaxItemTariffCode8'),
        ValofTaxGoods8: getMoney('ValofTaxGoods8'),
        ValofGoodsOwnUse8: getMoney('ValofGoodsOwnUse8'),
        ValTaxServices8: getMoney('ValTaxServices8'),
        TaxPayable8: getMoney('TaxPayable8'),
        AmtDeductCN8: getMoney('AmtDeductCN8'),
        TotalValExempted8: getMoney('TotalValExempted8'),

        TaxItemDesc9: getText('TaxItemDesc9'),
        TaxItemTariffCode9: getText('TaxItemTariffCode9'),
        ValofTaxGoods9: getMoney('ValofTaxGoods9'),
        ValofGoodsOwnUse9: getMoney('ValofGoodsOwnUse9'),
        ValTaxServices9: getMoney('ValTaxServices9'),
        TaxPayable9: getMoney('TaxPayable9'),
        AmtDeductCN9: getMoney('AmtDeductCN9'),
        TotalValExempted9: getMoney('TotalValExempted9'),

        TaxItemDesc10: getText('TaxItemDesc10'),
        TaxItemTariffCode10: getText('TaxItemTariffCode10'),
        ValofTaxGoods10: getMoney('ValofTaxGoods10'),
        ValofGoodsOwnUse10: getMoney('ValofGoodsOwnUse10'),
        ValTaxServices10: getMoney('ValTaxServices10'),
        TaxPayable10: getMoney('TaxPayable10'),
        AmtDeductCN10: getMoney('AmtDeductCN10'),
        TotalValExempted10: getMoney('TotalValExempted10'),

        // Part B2
        TotalValTaxablePerc5: getMoney('txtTotalValTaxablePerc5'),
        TotalValTaxPayablePerc5: getMoney('txtTotalValTaxPayablePerc5'),
        TotalValTaxablePerc10: getMoney('txtTotalValTaxablePerc10'),
        TotalValTaxPayablePerc10: getMoney('txtTotalValTaxPayablePerc10'),
        TotalValTaxablePerc6: getMoney('txtTotalValTaxablePerc6'),
        TotalValTaxPayablePerc6: getMoney('txtTotalValTaxPayablePerc6'),
        TotalValTaxablePerc8: getMoney('txtTotalValTaxablePerc8'),
        TotalValTaxPayablePerc8: getMoney('txtTotalValTaxPayablePerc8'),
        TotalUnitTaxableRM25: getText('txtTotalUnitTaxableRM25'),
        TotalValTaxPayableRM25: getMoney('txtTotalValTaxPayableRM25'),
        TotalValTaxPayable: getMoney('txtTotalValTaxPayable'),
        AmtDeductFrCreditNote: getMoney('txtAmtDeductFrCreditNote'),
        TotalValBadDebtRelief: getMoney('txtTotalValBadDebtRelief'),
        AmtDeductSalesTax: getMoney('txtAmtDeductSalesTax'),
        AmtDeductServiceTax: getMoney('txtAmtDeductServiceTax'),
        AmtAdjustmentSalesTax: getMoney('txtAmtAdjustmentSalesTax'),
        TotalTaxPayableB4Penalty: getMoney('txtTotalTaxPayableB4Penalty'),
        PenaltyRate: getMoney('txtPenaltyRate'),
        PenaltyAmt: getMoney('txtPenaltyAmt'),
        TotalTaxPayableInclPenalty: getMoney('txtTotalTaxPayableInclPenalty'),

        // Part C
        LitreRate: getMoney('txtLitreRate'),
        LitreSoldQty: getText('txtLitreSoldQty'),
        LitreValTaxSales: getMoney('txtLitreValTaxSales'),
        LitreValTaxPayable: getMoney('txtLitreValTaxPayable'),
        KGRate: getMoney('txtKGRate'),
        KGSoldQty: getText('txtKGSoldQty'),
        KGValTaxSales: getMoney('txtKGValTaxSales'),
        KGValTaxPayable: getMoney('txtKGValTaxPayable'),
        AdVolerumRate: Number(getText('txtAdVolerumRate')).toString(),
        AdVolValTaxSales: getMoney('txtAdVolValTaxSales'),
        AdVolValTaxPayable: getMoney('txtAdVolValTaxPayable'),

        // Part D
        SalesExemptedExport: getMoney('txtSalesExemptedExport'),
        SalesExemptedSchA: getMoney('txtSalesExemptedSchA'),
        SalesExemptedSchB: getMoney('txtSalesExemptedSchB'),
        SalesExemptedSchC_Item1and2: getMoney('txtSalesExemptedSchC_Item1and2'),
        SalesExemptedSchC_Item3and4: getMoney('txtSalesExemptedSchC_Item3and4'),
        SalesExemptedSchC_Item5: getMoney('txtSalesExemptedSchC_Item5'),
        TotalValExemptedTaxableServices: getMoney('txtTotalValExemptedTaxableServices'),
        TotalValExemptedGroupRelief: getMoney('txtTotalValExemptedGroupRelief'),
        TotalValPartialExemptedTaxableServices: getMoney('txtTotalValExemptedOthExemption'),
        TotalValNonTaxableServices: getMoney('txtTotalValExemptedNonTaxableServices'),
        TotalValExemptedSubsection35: getMoney('txtTotalValExemptedSubsection35'),

        // Part E
        PersonExempted_Item1and2: getMoney('txtPersonExempted_Item1and2'),
        PersonExempted_Item3and4: getMoney('txtPersonExempted_Item3and4'),
        PersonExempted_Item5: getMoney('txtPersonExempted_Item5'),

        // Part F
        FillingDate: getText('dtDate'),
        DeclarantName: getText('txtDeclarantName'),
        DeclarantNRIC: getText('MskNRICNo'),
        DeclarantDesignation: getText('txtDeclarantDesignation'),
        DeclarantTelNo: getText('txtDeclarantTelNo'),
      };

      return returnObj;
    },

    async getSSTRecon() {
      this.$fullscreenLoading('Getting SST Reconciliation');
      const requestBody = {
        startdate: this.reconStartDate.formatString('yyyy-MM-dd'),
        enddate: this.reconEndDate.formatString('yyyy-MM-dd'),
        showzero: this.reconIncludeZero ? '1' : '0',
      };

      const result = await this.$apiCallV2('/SSTReturn/PrintSSTRecon', requestBody);

      const { summary, detail } = result || {};
      const { value: reconType } = this.selectedReconType || {};
      const col2Name = reconType == 'Detail' ? 'Txn Date / Tax Period' : 'Txn Date';
      const col3Name = reconType == 'Detail' ? 'Doc No. / Filling No.' : 'Doc No.';

      const detailMap = detail.reduce((acc, d) => {
        if (!acc[d['Doc No']]) acc[d['Doc No']] = [];
        acc[d['Doc No']].push(d);
        return acc;
      }, {});

      const returnData = summary.flatMap((item, index) => {
        const {
          'Doc Type': DocType,
          'Doc No': DocNo,
          'Doc Date': DocDate,
          'Tax Amt': TaxAmt,
          'Paid Tax Amt': PaidTaxAmt,
          'Bal Tax Amt': BalTaxAmt,
        } = item;

        const returnItem = {
          '#': index + 1,
          [col2Name]: new Date(DocDate).formatString('yyyy-MM-dd'),
          [col3Name]: DocNo,
          Type: DocType,
          'Tax Amt.': TaxAmt,
          'Tax Amt Paid': PaidTaxAmt,
          'Bal Tax Amt.': BalTaxAmt,
        };

        if (reconType == 'Detail') {
          const detailRows = (detailMap[DocNo] ?? []).map((detailItem) => {
            const { FillingNo, FillingPeriodFrom, ReceiptNo, ReceiptDate, ReceiptOffsetAmount, ProrateItemSSTAmount } =
              detailItem;
            return {
              '#': '',
              [col2Name]: FillingPeriodFrom ? new Date(FillingPeriodFrom).formatString('yyyy-MM-dd') : '',
              [col3Name]: FillingNo,
              Type: '',
              'Tax Amt.': ProrateItemSSTAmount,
              'Tax Amt Paid': ReceiptOffsetAmount,
              'Bal Tax Amt.': '',
              'Offset Receipt': ReceiptNo,
              'Receipt Date': ReceiptDate ? new Date(ReceiptDate).formatString('yyyy-MM-dd') : '',
            };
          });

          return [{ ...returnItem, 'Offset Receipt': '', 'Receipt Date': '' }, ...detailRows];
        }

        return returnItem;
      });

      this.printTableData = returnData;

      const dataType = {
        '#': 'string',
        [col2Name]: 'date',
        [col3Name]: 'string',
        Type: 'string',
        'Tax Amt.': 'currency',
        'Tax Amt Paid': 'currency',
        'Bal Tax Amt.': 'currency',
        'Offset Receipt': 'string',
        'Receipt Date': 'date',
      };

      this.printTableDataType = dataType;

      this.showTable = true;
      // const data = {
      //   detailView: reconType == 'Detail',
      //   reconItem: summary.map((item, index) => {
      //     const {
      //       'Doc Date': txnDate,
      //       'Doc No': docNo,
      //       'Doc Type': type,
      //       'Tax Amt': taxAmt,
      //       'Paid Tax Amt': taxAmtPaid,
      //       'Bal Tax Amt': balTaxAmt,
      //     } = item;
      //     return {
      //       index: index + 1,
      //       txnDate: new Date(txnDate).formatString('dd-MM-yyyy'),
      //       docNo: docNo,
      //       type: type,
      //       taxAmt: this.$currencyFormat(taxAmt || 0),
      //       taxAmtPaid: this.$currencyFormat(taxAmtPaid || 0),
      //       balTaxAmt: this.$currencyFormat(balTaxAmt || 0),

      //       reconDetail: detail
      //         .filter((item) => item['Doc No'] == docNo && reconType == 'Detail')
      //         .map((item) => {
      //           const { FillingNo, FillingPeriodFrom, ProrateItemSSTAmount, ReceiptDate, ReceiptNo } = item;
      //           return {
      //             taxPeriod: FillingPeriodFrom ? new Date(FillingPeriodFrom).formatString('dd-MM-yyyy') : '',
      //             fillingNo: FillingNo ?? '',
      //             sstAmt: this.$currencyFormat(ProrateItemSSTAmount ?? 0),
      //             receiptNo: ReceiptNo ?? '',
      //             receiptDate: ReceiptDate ? new Date(ReceiptDate).formatString('dd-MM-yyyy') : '',
      //           };
      //         }),
      //     };
      //   }),

      //   grandTax: this.$currencyFormat(summary.reduce((acc, item) => acc + Number(item['Tax Amt'] || 0), 0)),
      //   grandTaxPaid: this.$currencyFormat(summary.reduce((acc, item) => acc + Number(item['Paid Tax Amt'] || 0), 0)),
      //   grandBalTax: this.$currencyFormat(summary.reduce((acc, item) => acc + Number(item['Bal Tax Amt'] || 0), 0)),
      // };

      // const general = {
      //   companyName: this.stateItem.loginDetails.CoName,
      //   printDate: this.reportDate(),
      //   startDate: new Date(this.reconStartDate).formatString('dd/MM/yyyy'),
      //   endDate: new Date(this.reconEndDate).formatString('dd/MM/yyyy'),
      //   page: '{{jsreportPageNum}} of {{jsreportTotalPage}}',
      // };

      // this.designerV3ReportFiles = ['SSTRecon.json'];
      // this.designerV3ReportData = [data];
      // this.designerV3SharedData = general;

      // this.showDesignerV3 = true;
      this.$fullscreenLoading();
    },

    sstRowSelected(e) {
      if (!e) return;
      this.reconStartDate = new Date(this.SSTDetails?.['SSTEffectiveDate']) || e['Period Start Date'];
      this.reconEndDate = new Date(e['Period End Date']);
    },

    async getSSTDetails() {
      const result = await this.$apiCallV2('/SSTReturn/GetSSTDetails', {});
      this.SSTDetails = (await result?.[0]) ?? null;
    },

    reportDate() {
      const date = new Date();

      // Day, Month, Year
      const dd = String(date.getDate()).padStart(2, '0');
      const MM = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const yyyy = date.getFullYear();

      // Hours, Minutes, Seconds
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');

      // AM/PM
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // convert 0 -> 12
      const hh = String(hours).padStart(2, '0');

      return `${dd}/${MM}/${yyyy} ${hh}:${minutes} ${ampm}`;
    },
  },

  async mounted() {
    this.$fullscreenLoading('Getting SST Returns');

    this.activeFillingNo = this.$route.query.fillingno?.trim() ?? null;
    await Promise.all([this.getSSTDetails(), this.getSSTReturnList()]);

    if (this.activeFillingNo) this.SSTListTableSelectedRows = this.SSTListTableData;
    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  font-size: 0.75rem;
  padding: 10px 45px;
  padding-right: 4px;

  display: flex;
  gap: 4px;

  width: 100%;
  height: 100%;

  .inner-container {
    height: 100%;
    width: 100%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;

    gap: 4px;
  }
}

.filters {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: end;
}

.user-instructions {
  display: inline;
  text-align: center;
  font-size: 20px;
  text-wrap: balance;
}
</style>
